import React, { useCallback, useEffect, useState } from 'react';
import type { ReactNode } from 'react';

import { Box, Col } from '@opendoor/atoms/box';
import { Combobox } from '@opendoor/atoms/combobox';
import type { ComboboxChangeEventDetails, ComboboxSize } from '@opendoor/atoms/combobox';
import { Loader } from '@opendoor/atoms/loader';
import { cn } from '@opendoor/atoms/utils';
import { recordSurveyAddressProvided } from '@opendoor/shared-fe/surveyAddressSession';
import { IconSearch } from '@tabler/icons-react';

import { validateAddress as validateAddressHelper } from '../../helpers/addressValidation';
import type { AddressValidationResult, SellerAddressLookup } from '../../helpers/addressValidation';
import type { SelectedAddress } from '../../helpers/AddressValidationContext';
import { ALLOWED_TYPES, buildAutocompleteUrl } from '../../helpers/googleMaps';
import { useRemoteAutocomplete } from '../../helpers/useRemoteAutocomplete';
import type { AutoCompletePrediction } from '../api/addressTypes';

/**
 * Google returns points of interest and partial streets alongside real
 * addresses; keep only entries specific enough to be a home. Casa suggestions
 * are already address-level and pass through.
 */
const selectAddressPredictions = (predictions: AutoCompletePrediction[]) =>
  predictions.filter((prediction) => {
    if (prediction.source !== 'google') {
      return true;
    }
    const terms = prediction.terms ?? [];
    const allowedTypes = (prediction.types ?? []).filter((type) => ALLOWED_TYPES.includes(type));
    return (
      (terms.length >= 5 && allowedTypes.length > 0) ||
      (terms.length === 4 && terms[0].value.match(/^\d+\S*\s\S+\s/) !== null)
    );
  });

const renderPrediction = (prediction: AutoCompletePrediction) => (
  <Combobox.Item key={prediction.id} value={prediction}>
    {prediction.description}
  </Combobox.Item>
);

export interface AddressInputProps {
  id: string;
  placeholder?: string;
  disabled?: boolean;
  isResolvingSelection?: boolean;
  hideLabel?: boolean;
  label?: string;
  /** Controlled search text. Pair with `onChange`. */
  value?: string;
  onChange?: (searchTerm: string) => void;
  /** Initial search text for uncontrolled use. Ignored when `value` is set. */
  defaultValue?: string;
  onAddressSelect: (address: SelectedAddress) => void;
  onAddressValidated?: (result: AddressValidationResult) => void;
  validateAddress?: boolean;
  addressLookup?: SellerAddressLookup;
  onError?: (error: Error) => void;
  onPredictionsChange?: (predictions: AutoCompletePrediction[]) => void;
  onSearchTermChange?: (searchTerm: string) => void;
  onFocus?: () => void;
  onBlur?: () => void;
  /** Extra content pinned below the suggestion list (e.g. a manual-entry CTA). */
  customAction?: ReactNode;
  listBoxAriaLabel: string;
  icon?: ReactNode;
  leadingIcon?: ReactNode;
  inputClassName?: string;
  popupClassName?: string;
  /** Replaces `icon` in the trailing slot. Receives a "submit best match" press handler. */
  actionButtonLayout?: 'inline' | 'responsive';
  actionButton?: (onPress: () => void, isLoading?: boolean) => ReactNode;
  /**
   * Hands the "submit best match" press handler to a CTA rendered outside the
   * field. The trailing slot stays empty in this mode, so the input keeps its
   * full width for text instead of reserving room for a suffix.
   */
  onSubmitHandlerChange?: (state: { onPress: () => void; isLoading: boolean }) => void;
  size?: ComboboxSize;
  error?: boolean;
}

/**
 * Address autocomplete: debounced suggestions from the `/_address-input` edge
 * worker, optionally resolved against casa before handing the address back.
 */
export const AddressInput: React.FC<AddressInputProps> = ({
  id,
  placeholder,
  disabled,
  isResolvingSelection = false,
  hideLabel,
  label,
  value,
  onChange,
  defaultValue,
  onAddressSelect,
  onAddressValidated,
  validateAddress = false,
  addressLookup,
  onError,
  onPredictionsChange,
  onSearchTermChange,
  onFocus,
  onBlur,
  customAction,
  listBoxAriaLabel,
  icon,
  leadingIcon,
  inputClassName,
  popupClassName,
  actionButton,
  actionButtonLayout = 'inline',
  onSubmitHandlerChange,
  size = 'md',
  error,
}) => {
  const [usedPrediction, setUsedPrediction] = useState<AutoCompletePrediction | null>(null);
  const [isFocused, setIsFocused] = useState(false);

  const {
    searchTerm,
    predictions,
    isLoading: isSearching,
    search,
    selectPrediction,
  } = useRemoteAutocomplete({
    buildUrl: buildAutocompleteUrl,
    selectPredictions: selectAddressPredictions,
    onError,
    value,
    defaultValue,
    onChange,
  });
  const isLoading = isSearching || isResolvingSelection;

  useEffect(() => {
    onPredictionsChange?.(predictions);
  }, [predictions, onPredictionsChange]);

  useEffect(() => {
    if (value === undefined) {
      onSearchTermChange?.(searchTerm);
    }
  }, [value, searchTerm, onSearchTermChange]);

  const handleSelectedAddress = useCallback(
    async (address: SelectedAddress) => {
      if (validateAddress && onAddressValidated) {
        try {
          onAddressValidated(await validateAddressHelper(address, addressLookup));
        } catch (validationError) {
          onError?.(validationError as Error);
        }
      }
      onAddressSelect(address);
    },
    [validateAddress, addressLookup, onAddressValidated, onAddressSelect, onError],
  );

  const handlePredictionSelect = useCallback(
    (prediction: AutoCompletePrediction) => {
      if (disabled) return;
      // A selected suggestion must not reopen over the confirmation modal.
      setIsFocused(false);
      recordSurveyAddressProvided();
      setUsedPrediction(prediction);
      return selectPrediction<SelectedAddress>(prediction, handleSelectedAddress);
    },
    [disabled, selectPrediction, handleSelectedAddress],
  );

  const handleInputValueChange = (nextValue: string, details: ComboboxChangeEventDetails) => {
    if (details.reason === 'input-change' && !disabled) setIsFocused(true);
    search(nextValue, details);
  };

  const handleValueChange = (prediction: AutoCompletePrediction | null) => {
    if (prediction) {
      void handlePredictionSelect(prediction);
    }
  };

  const handleInputFocus = () => {
    setIsFocused(true);
    onFocus?.();
  };

  const handleInputBlur = () => {
    setIsFocused(false);
    onBlur?.();
  };

  const displayIcon = isResolvingSelection ? (
    <span aria-hidden>
      <Loader size="sm" />
    </span>
  ) : (
    (icon ??
    (isLoading ? (
      <Loader size="sm" />
    ) : (
      <IconSearch size={20} className="text-od-content-primary" />
    )))
  );

  const handleSubmitPress = useCallback(() => {
    if (predictions.length && predictions[0]) {
      void handlePredictionSelect(predictions[0]);
    } else if (usedPrediction && searchTerm === usedPrediction.description) {
      void handlePredictionSelect(usedPrediction);
    }
  }, [predictions, usedPrediction, searchTerm, handlePredictionSelect]);

  useEffect(() => {
    onSubmitHandlerChange?.({ onPress: handleSubmitPress, isLoading });
  }, [onSubmitHandlerChange, handleSubmitPress, isLoading]);

  let suffix: ReactNode = onSubmitHandlerChange ? null : displayIcon;
  if (actionButton && !onSubmitHandlerChange) {
    suffix = actionButton(handleSubmitPress, isLoading);
  }

  const responsiveAction =
    actionButtonLayout === 'responsive' && actionButton && !onSubmitHandlerChange;

  // Base UI would open the popup on click, but suggestions arrive from the
  // network, so opening is gated on there actually being something to show.
  const open =
    !disabled &&
    isFocused &&
    searchTerm !== '' &&
    (predictions.length > 0 || Boolean(customAction));

  return (
    <Combobox.Root<AutoCompletePrediction>
      id={id}
      size={size}
      items={predictions}
      // Suggestions are already filtered server-side; filtering again would drop
      // results whose label does not literally contain the query.
      filter={null}
      inputValue={searchTerm}
      onInputValueChange={handleInputValueChange}
      open={open}
      disabled={disabled}
      itemToStringLabel={(prediction) => prediction.description}
      isItemEqualToValue={(a, b) => a.id === b.id}
      onValueChange={handleValueChange}
    >
      <Col className="relative w-full gap-3">
        <Box className="relative w-full">
          <Combobox.Input
            className={cn(leadingIcon && 'pl-12', responsiveAction && 'sm:pr-32', inputClassName)}
            placeholder={placeholder}
            error={error}
            suffix={responsiveAction ? null : suffix}
            aria-busy={isLoading}
            aria-label={hideLabel || !label ? (label ?? 'Address') : undefined}
            onFocus={handleInputFocus}
            onBlur={handleInputBlur}
          />
          {leadingIcon && (
            <Box
              aria-hidden="true"
              className="pointer-events-none absolute inset-y-0 left-4 items-center text-od-content-primary"
            >
              {leadingIcon}
            </Box>
          )}
        </Box>
        {responsiveAction && <Box className="sm:absolute sm:top-1.5 sm:right-2">{suffix}</Box>}
      </Col>
      <Combobox.Popup aria-label={listBoxAriaLabel} className={popupClassName}>
        <Combobox.List>{renderPrediction}</Combobox.List>
        {customAction ? <Box>{customAction}</Box> : null}
      </Combobox.Popup>
    </Combobox.Root>
  );
};
